import React, { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Star, Plus, Edit, Trash2, Video, Lock, AlertCircle, Target, Palette, X } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { useToast } from "@/hooks/use-toast";
import { MessageSquare, BarChart3, Download, Calendar, CheckCircle, Clock, Home, LogOut, Users, Settings, DollarSign, TrendingUp, AlertTriangle, ChevronDown, MessageCircle, PhoneCall } from "lucide-react";
import SessionCompletionModal from "@/components/SessionCompletionModal";
import ReportSessionModal from "@/components/ReportSessionModal";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import AdvancedAvailabilityManager from "@/components/AdvancedAvailabilityManager";
import { getUserTimezone, getTimezoneAbbr } from "@/lib/timezone-utils";
import MentorProfileUpload from "@/components/MentorProfileUpload";
import { SecurityQuestionSetup } from "@/components/SecurityQuestionSetup";
import { PasswordResetForm } from "@/components/PasswordResetForm";
import { MentorTierSelector } from "@/components/MentorTierSelector";
import { MentorTierBadge } from "@/components/MentorTierBadge";
import { MentorPayoutTransparency } from "@/components/MentorPayoutTransparency";
import { MentorInvoiceManager } from "@/components/MentorInvoiceManager";
import { MentorProfileEditor } from "@/components/MentorProfileEditor";
import { NotificationBell } from "@/components/NotificationBell";
import { MentorReviews } from "@/components/MentorReviews";
import { BrandingCustomizer } from "@/components/BrandingCustomizer";
import { MentorVisibilityToggle } from "@/components/MentorVisibilityToggle";
import { DiscoveryCallToggle } from "@/components/DiscoveryCallToggle";
import { BookingInsights } from "@/components/BookingInsights";
import { ProAnalyticsDashboard } from "@/components/ProAnalyticsDashboard";
import { MentorProgramManager } from "@/components/MentorProgramManager";
import { W9Upload } from "@/components/W9Upload";
import { MentorApplicationModal } from "@/components/MentorApplicationModal";
import { AccountDeactivation } from "@/components/AccountDeactivation";
import { formatTime12Hour } from "@/lib/utils";
import { safeGetItem, safeRemoveItem } from "@/lib/safe-storage";
import ComplianceFooter from "@/components/ComplianceFooter";

interface AvailabilitySlot {
  day: string;
  startTime: string;
  endTime: string;
  isEnabled: boolean;
}


const MentorDashboard = () => {
  // Set page title
  useEffect(() => {
    document.title = "Mentor Dashboard - Ascendry";
  }, []);
  
  const [mentorProfile, setMentorProfile] = useState<{first_name: string; last_name: string; user_id: string; email: string; role: string} | null>(null);
  const [mentorData, setMentorData] = useState<any>(null);
  const [sessions, setSessions] = useState<any[]>([]);
  const [needsTierSelection, setNeedsTierSelection] = useState(false);
  const [showApplicationModal, setShowApplicationModal] = useState(false);
  const [hasSubmittedApplication, setHasSubmittedApplication] = useState(false);
  const [searchParams] = useSearchParams();
  const [stats, setStats] = useState({
    totalEarnings: 0,
    completedSessions: 0,
    averageRating: 0,
    upcomingSessions: 0
  });
  const [sessionToComplete, setSessionToComplete] = useState<any>(null);
  const [sessionToReport, setSessionToReport] = useState<any>(null);
  
  // Collapsible states
  const [payoutOpen, setPayoutOpen] = useState(false);
  const [businessOpen, setBusinessOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [proFeaturesOpen, setProFeaturesOpen] = useState(false);
  
  const { toast } = useToast();
  const navigate = useNavigate();

  // Check for booking continuation
  useEffect(() => {
    const handleBookingContinuation = async () => {
      const bookingData = safeGetItem('pendingBooking');
      if (bookingData) {
        const { mentorId, timeSlot, notes } = JSON.parse(bookingData);
        if (mentorId) {
          navigate(`/mentor/${mentorId}?timeSlot=${encodeURIComponent(timeSlot)}&notes=${encodeURIComponent(notes || '')}`);
          safeRemoveItem('pendingBooking');
        }
      }
    };
    
    handleBookingContinuation();
  }, [navigate]);

  const handleSignOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      
      toast({
        title: "Signed Out",
        description: "You have been successfully signed out.",
      });
      
      navigate('/');
    } catch (error: any) {
      toast({
        title: "Error",
        description: "Failed to sign out. Please try again.",
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    const fetchMentorData = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) {
        navigate('/auth/mentor');
        return;
      }

      // Check if user is actually a mentor or admin
      const { data: profile } = await supabase
        .from('profiles')
        .select('role, first_name, last_name, user_id, email')
        .eq('user_id', session.user.id)
        .single();
      
      if (!profile || (profile.role !== 'mentor' && profile.role !== 'admin')) {
        toast({
          title: "Access Denied",
          description: "You need to be a mentor to access this page.",
          variant: "destructive",
        });
        navigate('/auth/mentor');
        return;
      }

      setMentorProfile(profile);

      // Skip mentor application requirement for admins
      if (profile.role !== 'admin') {
        // Check if user has submitted a mentor application
        const { data: application } = await supabase
          .from('mentor_applications')
          .select('id, status')
          .eq('email', profile.email)
          .single();

        if (!application) {
          // No application submitted - show modal
          setShowApplicationModal(true);
          return;
        }
      }

      setHasSubmittedApplication(true);

      // Fetch mentor data including Zoom connection
      const { data: mentor } = await supabase
        .from('mentors')
        .select('*')
        .eq('user_id', session.user.id)
        .single();
      
if (mentor) {
  setMentorData(mentor);

  // Auto-set mentor timezone if missing
  if (!mentor.timezone) {
    try {
      const tz = getUserTimezone();
      const { error: tzError } = await supabase
        .from('mentors')
        .update({ timezone: tz })
        .eq('id', mentor.id);
      if (!tzError) {
        setMentorData((prev: any) => (prev ? { ...prev, timezone: tz } : prev));
      }
    } catch (e) {
      console.error('Failed to set mentor timezone:', e);
    }
  }
  
  // Check if mentor is approved and has selected a tier
  if (mentor.is_approved && !mentor.tier_selected_at) {
    setNeedsTierSelection(true);
  } else if (mentor.is_approved) {
    fetchMentorSessions(mentor.id);
    fetchMentorStats(mentor.id);
  }
}
    };

    fetchMentorData();
  }, [navigate, toast]);

  const fetchMentorSessions = async (mentorId: string) => {
    try {
      console.log('Fetching sessions for mentor ID:', mentorId);
      const { data, error } = await supabase
        .from('sessions')
        .select(`
          *,
          profiles!sessions_student_id_fkey (first_name, last_name, email, phone)
        `)
        .eq('mentor_id', mentorId)
        .neq('status', 'cancelled') // Filter out cancelled sessions
        .order('scheduled_at', { ascending: true });
      
      if (error) throw error;
      console.log('Sessions fetched:', data);
      setSessions(data || []);
    } catch (error) {
      console.error('Error fetching sessions:', error);
    }
  };

  const fetchMentorStats = async (mentorId: string) => {
    try {
      const { data: completedSessions, error } = await supabase
        .from('sessions')
        .select('mentor_payout_amount')
        .eq('mentor_id', mentorId)
        .eq('status', 'completed');
      
      if (error) throw error;

      const totalEarnings = completedSessions?.reduce((sum, session) => 
        sum + (session.mentor_payout_amount || 0), 0
      ) || 0;

      const upcomingSessions = sessions.filter(s => 
        s.status === 'scheduled' && new Date(s.scheduled_at) > new Date()
      ).length;

      setStats({
        totalEarnings, // mentor_payout_amount is already stored in dollars
        completedSessions: completedSessions?.length || 0,
        averageRating: mentorData?.rating || 0,
        upcomingSessions
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleMentorUpdate = async () => {
    // Refetch mentor data when Zoom connection changes
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.user) {
      const { data: mentor } = await supabase
        .from('mentors')
        .select('*')
        .eq('user_id', session.user.id)
        .single();
      
      if (mentor) {
        setMentorData(mentor);
      }
    }
  };

  const updateSessionStatus = async (sessionId: string, status: 'in_progress') => {
    try {
      const { error } = await supabase.functions.invoke('update-session-status', {
        body: {
          sessionId,
          status,
        }
      });

      if (error) throw error;

      toast({
        title: "Session Updated",
        description: `Session status updated to ${status}.`,
      });

      // Refresh data
      if (mentorData) {
        fetchMentorSessions(mentorData.id);
        fetchMentorStats(mentorData.id);
      }
    } catch (error: any) {
      toast({
        title: "Error",
        description: "Failed to update session status.",
        variant: "destructive",
      });
    }
  };

  const handleCompleteSession = () => {
    // Refresh data after completion
    if (mentorData) {
      fetchMentorSessions(mentorData.id);
      fetchMentorStats(mentorData.id);
    }
  };

  const downloadCalendarFile = async (sessionId: string) => {
    console.log('🔍 Mentor calendar download function called with sessionId:', sessionId);
    console.log('🔍 Function type:', typeof sessionId);
    
    if (!sessionId) {
      console.error('❌ No sessionId provided');
      toast({
        title: "Error",
        description: "No session ID provided for calendar download.",
        variant: "destructive",
      });
      return;
    }

    try {
      console.log('📅 Starting mentor calendar download for session:', sessionId);
      console.log('📅 Using direct fetch approach...');
      
      // Get current session for auth
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.access_token) {
        throw new Error('Not authenticated');
      }
      
      // Make direct fetch to edge function
      const response = await fetch(`https://llcojpvgertoimsualob.supabase.co/functions/v1/generate-calendar-file`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
          'apikey': 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImxsY29qcHZnZXJ0b2ltc3VhbG9iIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTQyNzMwODAsImV4cCI6MjA2OTg0OTA4MH0.mPKMSynGdAZknGpnaZFqY9Xa1S8cxg2u1LlpJfiRc5I'
        },
        body: JSON.stringify({ sessionId })
      });

      console.log('📅 Direct fetch response:', { 
        status: response.status, 
        ok: response.ok,
        statusText: response.statusText 
      });

      if (!response.ok) {
        const errorText = await response.text().catch(() => 'Unknown error');
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      // Get the ICS content as text
      const icsContent = await response.text();
      console.log('📅 Received ICS content length:', icsContent.length);

      if (!icsContent || icsContent.length === 0) {
        throw new Error('Empty calendar content received');
      }

      console.log('📅 Creating mentor blob and download...');
      
      // Create blob and download
      const blob = new Blob([icsContent], { type: 'text/calendar; charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `mentoring-session-${sessionId}.ics`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      console.log('📅 Mentor download completed successfully');

      toast({
        title: "Calendar File Downloaded",
        description: "The session has been added to your calendar file.",
      });
    } catch (error: any) {
      console.error('📅 Mentor calendar download error:', error);
      console.error('📅 Mentor error stack:', error.stack);
      console.error('📅 Mentor error name:', error.name);
      console.error('📅 Mentor error message:', error.message);
      
      toast({
        title: "Download Failed",
        description: error.message || "Failed to download calendar file. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleTierSelected = async (tier: 'free' | 'pro') => {
    try {
      // Update mentor tier selection in database
      const { error } = await supabase
        .from('mentors')
        .update({
          tier,
          tier_selected_at: new Date().toISOString()
        })
        .eq('user_id', mentorData.user_id);

      if (error) throw error;

      // Update local state
      setMentorData(prev => ({
        ...prev,
        tier,
        tier_selected_at: new Date().toISOString()
      }));
      
      setNeedsTierSelection(false);

      toast({
        title: "Tier Selected!",
        description: tier === 'free' 
          ? "You're now on the Free tier. Start mentoring with a 7.5% platform fee."
          : "You're now on the Pro tier with no platform fees!",
      });

      // Fetch sessions and stats now that tier is selected
      fetchMentorSessions(mentorData.id);
      fetchMentorStats(mentorData.id);
    } catch (error) {
      console.error('Error selecting tier:', error);
      toast({
        title: "Error",
        description: "Failed to select tier. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleApplicationSubmitted = () => {
    setShowApplicationModal(false);
    setHasSubmittedApplication(true);
    toast({
      title: "Application Submitted!",
      description: "Your application is now being reviewed. You'll be notified once approved.",
    });
  };

  // Show tier selector if needed
  if (needsTierSelection && mentorProfile && mentorData) {
    return (
      <MentorTierSelector 
        onTierSelected={handleTierSelected}
        mentorName={`${mentorProfile.first_name} ${mentorProfile.last_name}`}
      />
    );
  }
  
  // Show application modal for new mentors
  if (showApplicationModal && mentorProfile) {
    return (
      <MentorApplicationModal
        isOpen={showApplicationModal}
        mentorProfile={mentorProfile}
        onApplicationSubmitted={handleApplicationSubmitted}
        onSignOut={handleSignOut}
      />
    );
  }

  // Show "application submitted" message if application exists but mentor record doesn't
  // BUT skip this for admins - they should see the dashboard even without a mentor record
  if (hasSubmittedApplication && !mentorData && mentorProfile?.role !== 'admin') {
    return (
      <div className="min-h-screen bg-gradient-dark flex items-center justify-center p-4">
        <Card className="max-w-2xl w-full obsidian-card">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className="rounded-full bg-blue-100 p-3">
                <Clock className="h-8 w-8 text-blue-600" />
              </div>
            </div>
            <CardTitle className="text-2xl">Application Submitted</CardTitle>
            <CardDescription className="text-lg">
              Thank you for submitting your mentor application!
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <div className="bg-muted/50 rounded-lg p-4">
              <p className="text-muted-foreground">
                Your mentor application has been successfully submitted and is currently being reviewed by our team. 
                You'll receive an email notification once your application has been approved.
              </p>
            </div>
            
            <div className="space-y-2">
              <p className="font-medium">What happens next?</p>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Our team will review your application and credentials</li>
                <li>• You'll receive an email once approved</li>
                <li>• Full dashboard access will be unlocked</li>
                <li>• You can start scheduling mentoring sessions</li>
              </ul>
            </div>

            <div className="pt-4 border-t">
              <Button variant="outline" onClick={handleSignOut}>
                <LogOut className="w-4 h-4 mr-2" />
                Sign Out
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
  // Show approval pending message for unapproved mentors
  if (mentorData && !mentorData.is_approved) {
    return (
      <div className="min-h-screen bg-gradient-dark flex items-center justify-center p-4">
        <Card className="max-w-2xl w-full obsidian-card">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className="rounded-full bg-yellow-100 p-3">
                <Clock className="h-8 w-8 text-yellow-600" />
              </div>
            </div>
            <CardTitle className="text-2xl">Application Under Review</CardTitle>
            <CardDescription className="text-lg">
              Thank you for applying to become a mentor!
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <div className="bg-muted/50 rounded-lg p-4">
              <p className="text-muted-foreground">
                Your mentor application is currently being reviewed by our team. 
                You'll receive an email notification once your application has been approved.
              </p>
            </div>
            
            <div className="space-y-2">
              <p className="font-medium">What happens next?</p>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>• Our team will review your application</li>
                <li>• You'll receive an email once approved</li>
                <li>• Full dashboard access will be unlocked</li>
                <li>• You can start scheduling mentoring sessions</li>
              </ul>
            </div>

            <div className="pt-4 border-t">
              <Button variant="outline" onClick={handleSignOut}>
                <LogOut className="w-4 h-4 mr-2" />
                Sign Out
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }


  return (
    <div className="min-h-screen bg-gradient-dark">
      {/* Header */}
      <header className="border-b border-border bg-background/95 backdrop-blur-xl shadow-lg">
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-2 md:space-x-4">
              <div className="flex items-center gap-3">
                <img 
                  src="/lovable-uploads/e601b2a4-d63b-447f-b8f5-3e03625e19d6.png" 
                  alt="Ascendry" 
                  className="h-8 md:h-10 w-auto"
                />
                <span className="hidden md:block text-2xl font-bold text-glow">
                  Ascendry
                </span>
              </div>
              <Badge variant="secondary" className="bg-mentor-primary/10 text-mentor-primary hidden sm:inline-flex">
                Mentor Dashboard
              </Badge>
              {mentorData && (
                <MentorTierBadge 
                  tier={mentorData.tier} 
                  platformFeeExempt={mentorData.platform_fee_exempt}
                  className="hidden lg:inline-flex"
                />
              )}
            </div>
            <div className="flex items-center space-x-1 md:space-x-4">
              <Button variant="ghost" size="sm" asChild className="hidden md:flex">
                <Link to="/">
                  <Home className="w-4 h-4 mr-2" />
                  Home
                </Link>
              </Button>
              <Button variant="ghost" size="sm" asChild className="md:hidden">
                <Link to="/">
                  <Home className="w-4 h-4" />
                </Link>
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                asChild 
                className="text-purple-400 hover:text-purple-300 hover:bg-purple-500/10"
                title="Join our Discord community"
              >
                <a href="https://discord.gg/Ump5j4TYZu" target="_blank" rel="noopener noreferrer">
                  <MessageCircle className="w-4 h-4 mr-2" />
                  <span className="hidden md:inline">Discord</span>
                </a>
              </Button>
              <NotificationBell />
              <span className="text-muted-foreground hidden lg:inline">
                {mentorProfile ? `${mentorProfile.first_name} ${mentorProfile.last_name}` : 'Loading...'}
              </span>
              <Button variant="outline" size="sm" onClick={handleSignOut} className="hidden md:flex">
                <LogOut className="w-4 h-4 mr-2" />
                Sign Out
              </Button>
              <Button variant="outline" size="sm" onClick={handleSignOut} className="md:hidden">
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8 space-y-8">
        {/* Dashboard Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-glow">Mentor Dashboard</h1>
            <p className="text-muted-foreground mt-1">Manage your mentoring business</p>
          </div>
        </div>

        {/* Session History Section */}
        <section>
          <h2 className="text-xl font-semibold text-energy mb-4">Session History</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Upcoming Sessions */}
            <Card className="obsidian-card">
              <CardHeader>
                <CardTitle className="text-energy">Upcoming Sessions</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {sessions.filter(s => ['pending', 'scheduled', 'in_progress'].includes(s.status)).length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      No upcoming sessions scheduled
                    </div>
                  ) : (
                    sessions
                      .filter(s => ['pending', 'scheduled', 'in_progress'].includes(s.status))
                      .slice(0, 5)
                      .map((session) => (
                        <div key={session.id} className="border rounded-lg p-4 space-y-3">
                           <div className="flex items-center justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <h4 className="font-medium">
                                  {session.profiles?.first_name} {session.profiles?.last_name}
                                </h4>
                                {session.is_discovery_call && (
                                  <Badge variant="secondary" className="bg-mentor-primary/20 text-mentor-primary text-xs">
                                    <PhoneCall className="w-3 h-3 mr-1" />
                                    Discovery Call
                                  </Badge>
                                )}
                              </div>
                              <p className="text-sm text-muted-foreground">
                                {new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeZone: mentorData?.timezone || getUserTimezone() }).format(new Date(session.scheduled_at))} at {new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit', timeZone: mentorData?.timezone || getUserTimezone() }).format(new Date(session.scheduled_at))} {getTimezoneAbbr(mentorData?.timezone || getUserTimezone())}
                              </p>
                              {session.profiles?.email && (
                                <p className="text-xs text-muted-foreground">
                                  📧 {session.profiles.email}
                                </p>
                              )}
                              {session.profiles?.phone && (
                                <p className="text-xs text-muted-foreground">
                                  📱 {session.profiles.phone}
                                </p>
                              )}
                            </div>
                            <Badge variant={
                              session.status === 'pending' ? 'secondary' : 
                              session.status === 'scheduled' ? 'outline' : 
                              'default'
                            }>
                              {session.status === 'pending' ? 'Pending Confirmation' : session.status}
                            </Badge>
                          </div>
                          <div className="text-sm">
                            <p><strong>Duration:</strong> {session.duration_minutes || 60} minutes</p>
                            <p><strong>Notes:</strong> {session.notes || 'No notes provided'}</p>
                            {session.status === 'pending' && session.confirmation_deadline && (
                              <p className="text-amber-500 font-medium mt-2">
                                ⏰ Confirm by: {new Date(session.confirmation_deadline).toLocaleString()}
                              </p>
                            )}
                          </div>
                           <div className="flex gap-2 flex-wrap">
                            {/* Join Video Call Button - Always visible for scheduled sessions */}
                            {session.status === 'scheduled' && (() => {
                              const sessionDate = new Date(session.scheduled_at);
                              const now = new Date();
                              const minutesUntilSession = (sessionDate.getTime() - now.getTime()) / (1000 * 60);
                              const canJoin = true; // TEMPORARY: Always enabled for testing
                              
                              return (
                                <Button
                                  size="sm"
                                  variant={canJoin ? "default" : "outline"}
                                  onClick={() => navigate(`/session/${session.id}`)}
                                  disabled={!canJoin}
                                  className={cn(
                                    "flex items-center gap-2 font-medium",
                                    canJoin && "bg-green-600 hover:bg-green-700 text-white animate-pulse"
                                  )}
                                  title={
                                    !canJoin 
                                      ? minutesUntilSession > 15
                                        ? `Available 15 min before session (${Math.floor(Math.abs(minutesUntilSession / 60))}h ${Math.floor(Math.abs(minutesUntilSession % 60))}m)` 
                                        : "Session has ended"
                                      : "Click to join video call"
                                  }
                                >
                                  <Video className="w-4 h-4" />
                                  {canJoin ? "🔴 Join Video Call" : `Join in ${Math.floor(minutesUntilSession / 60)}h ${Math.floor(minutesUntilSession % 60)}m`}
                                </Button>
                              );
                            })()}
                            
                            {session.status === 'pending' && (
                              <>
                                <Button
                                  size="sm"
                                  variant="default"
                                  onClick={async () => {
                                    try {
                                      const { data, error } = await supabase.functions.invoke('confirm-session', {
                                        body: { sessionId: session.id }
                                      });
                                      
                                      if (error) {
                                        console.error('Edge function error:', error);
                                        throw error;
                                      }
                                      
                                      // Check if the response contains an error
                                      if (data && 'error' in data) {
                                        console.error('Session confirmation error:', data);
                                        throw new Error(data.error + (data.details ? `: ${data.details}` : ''));
                                      }
                                      
                                      toast({
                                        title: "Session Confirmed",
                                        description: "The student has been notified.",
                                      });
                                      if (mentorData) {
                                        fetchMentorSessions(mentorData.id);
                                        fetchMentorStats(mentorData.id);
                                      }
                                    } catch (error: any) {
                                      console.error('Error confirming session:', error);
                                      const errorMessage = error?.message || "Failed to confirm session. Please try again.";
                                      toast({
                                        title: "Error",
                                        description: errorMessage,
                                        variant: "destructive",
                                      });
                                    }
                                  }}
                                  className="bg-green-600 hover:bg-green-700"
                                >
                                  <CheckCircle className="w-4 h-4 mr-2" />
                                  Confirm
                                </Button>
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  onClick={async () => {
                                    const reason = prompt("Optional: Provide a reason for denying this session");
                                    try {
                                      const { error } = await supabase.functions.invoke('deny-session', {
                                        body: { sessionId: session.id, reason }
                                      });
                                      if (error) throw error;
                                      toast({
                                        title: "Session Denied",
                                        description: "The student has been notified and credits refunded.",
                                      });
                                      if (mentorData) {
                                        fetchMentorSessions(mentorData.id);
                                        fetchMentorStats(mentorData.id);
                                      }
                                    } catch (error) {
                                      console.error('Error denying session:', error);
                                      toast({
                                        title: "Error",
                                        description: "Failed to deny session. Please try again.",
                                        variant: "destructive",
                                      });
                                    }
                                  }}
                                >
                                  <X className="w-4 h-4 mr-2" />
                                  Deny
                                </Button>
                              </>
                            )}
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => downloadCalendarFile(session.id)}
                              className="flex items-center gap-2"
                            >
                              <Download className="w-4 h-4" />
                              Calendar
                            </Button>
                          </div>
                        </div>
                      ))
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Completed Sessions */}
            <Card className="obsidian-card">
              <CardHeader>
                <CardTitle className="text-energy">Completed Sessions</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {sessions.filter(s => s.status === 'completed').length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      No completed sessions yet
                    </div>
                  ) : (
                    sessions
                      .filter(s => s.status === 'completed')
                      .slice(0, 5)
                      .map((session) => (
                        <div key={session.id} className="border rounded-lg p-4 space-y-2">
                          <div className="flex items-center justify-between">
                            <div>
                              <h4 className="font-medium">
                                {session.profiles?.first_name} {session.profiles?.last_name}
                              </h4>
                              <p className="text-sm text-muted-foreground">
                                Completed: {new Date(session.completed_at || session.scheduled_at).toLocaleDateString()}
                              </p>
                              {session.profiles?.email && (
                                <p className="text-xs text-muted-foreground">
                                  📧 {session.profiles.email}
                                </p>
                              )}
                              {session.profiles?.phone && (
                                <p className="text-xs text-muted-foreground">
                                  📱 {session.profiles.phone}
                                </p>
                              )}
                            </div>
                            <Badge variant="default" className="bg-mentor-primary/10 text-mentor-primary">
                              Completed
                            </Badge>
                          </div>
                           <div className="text-sm space-y-1">
                             <p><strong>Duration:</strong> {session.duration_minutes || 60} minutes</p>
                             <p><strong>Earnings:</strong> ${(session.mentor_payout_amount ?? 0).toFixed(2)}</p>
                             {session.notes && <p><strong>Notes:</strong> {session.notes}</p>}
                           </div>
                           <div className="flex gap-2 mt-2">
                             <Button
                               size="sm"
                               variant="outline"
                               onClick={() => setSessionToReport(session)}
                               className="flex items-center gap-1"
                             >
                               <AlertTriangle className="w-3 h-3" />
                               Report Issue
                             </Button>
                           </div>
                        </div>
                      ))
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Performance Overview Section */}
        <section>
          <h2 className="text-xl font-semibold text-energy mb-4">Performance Overview</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="obsidian-card">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-energy">All-Time Earnings</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">${stats.totalEarnings.toFixed(2)}</div>
                <p className="text-xs text-muted-foreground">
                  {stats.totalEarnings === 0 ? 'No sessions completed yet' : 'From all completed sessions'}
                </p>
              </CardContent>
            </Card>

            <Card className="obsidian-card">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-energy">Total Sessions</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.completedSessions}</div>
                <p className="text-xs text-muted-foreground">
                  {stats.completedSessions === 0 ? 'No sessions yet' : 'Completed sessions'}
                </p>
              </CardContent>
            </Card>

            <Card className="obsidian-card">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-energy">Average Rating</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {stats.averageRating ? stats.averageRating.toFixed(1) : '-'}
                </div>
                <p className="text-xs text-muted-foreground">
                  {stats.averageRating ? 'From student reviews' : 'No reviews yet'}
                </p>
              </CardContent>
            </Card>

            {/* Discord Community Card */}
            <Card className="obsidian-card border-purple-500/30 bg-gradient-to-br from-purple-900/20 to-indigo-900/20">
              <CardContent className="p-6 text-center space-y-4">
                <div className="w-12 h-12 mx-auto bg-purple-500/20 rounded-full flex items-center justify-center">
                  <MessageCircle className="w-6 h-6 text-purple-400" />
                </div>
                <div>
                  <h3 className="font-bold text-purple-300 text-sm mb-2">Join Discord</h3>
                  <p className="text-xs text-muted-foreground mb-3">
                    Connect with the community
                  </p>
                  <Button 
                    asChild 
                    size="sm" 
                    className="w-full bg-purple-600 hover:bg-purple-700 text-white text-xs"
                  >
                    <a href="https://discord.gg/Ump5j4TYZu" target="_blank" rel="noopener noreferrer">
                      <MessageCircle className="w-3 h-3 mr-1" />
                      Join Now
                    </a>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Recent Completed Sessions - moved into Performance Overview */}
          <Card className="obsidian-card mt-6">
            <CardHeader>
              <CardTitle className="text-energy">Recent Completed Sessions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {sessions.filter(s => s.status === 'completed').length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    No completed sessions yet
                  </div>
                ) : (
                  sessions
                    .filter(s => s.status === 'completed')
                    .slice(0, 5)
                    .map((session) => (
                      <div key={session.id} className="border rounded-lg p-4 space-y-2">
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-medium">
                              {session.profiles?.first_name} {session.profiles?.last_name}
                            </h4>
                            <p className="text-sm text-muted-foreground">
                              {new Date(session.completed_at).toLocaleDateString()}
                            </p>
                          </div>
                          <div className="text-right">
                            <Badge variant="default" className="bg-mentor-primary/10 text-mentor-primary">
                              Completed
                            </Badge>
                            <p className="text-sm text-muted-foreground mt-1">
                              ${(session.mentor_payout_amount ?? 0).toFixed(2)} earned
                            </p>
                          </div>
                        </div>
                         <div className="text-sm space-y-1">
                           <p><strong>Duration:</strong> {session.duration_minutes} minutes</p>
                           <p><strong>Earnings:</strong> ${(session.mentor_payout_amount ?? 0).toFixed(2)}</p>
                           {session.notes && <p><strong>Notes:</strong> {session.notes}</p>}
                         </div>
                         <div className="flex gap-2 mt-2">
                           <Button
                             size="sm"
                             variant="outline"
                             onClick={() => setSessionToReport(session)}
                             className="flex items-center gap-1"
                           >
                             <AlertTriangle className="w-3 h-3" />
                             Report Issue
                           </Button>
                         </div>
                      </div>
                    ))
                )}
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Collapsible Sections */}
        <div className="space-y-6">
          {/* Payout Rate Transparency - Now Collapsible */}
          <Collapsible open={payoutOpen} onOpenChange={setPayoutOpen}>
            <CollapsibleTrigger asChild>
              <Button variant="ghost" className="w-full justify-between text-left p-4 sm:p-6 h-auto obsidian-card hover:bg-muted/5">
                <h3 className="text-xl font-semibold text-energy">Payout Rate Transparency</h3>
                <ChevronDown className={`h-5 w-5 text-primary transition-transform duration-200 flex-shrink-0 ${payoutOpen ? 'rotate-180' : ''}`} />
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="pt-4">
              {mentorData && <MentorPayoutTransparency mentorId={mentorData.id} />}
            </CollapsibleContent>
          </Collapsible>

          {/* Business Management - Now Collapsible */}
          <Collapsible open={businessOpen} onOpenChange={setBusinessOpen}>
            <CollapsibleTrigger asChild>
              <Button variant="ghost" className="w-full justify-between text-left p-4 sm:p-6 h-auto obsidian-card hover:bg-muted/5">
                <h3 className="text-xl font-semibold text-energy">Business Management</h3>
                <ChevronDown className={`h-5 w-5 text-primary transition-transform duration-200 flex-shrink-0 ${businessOpen ? 'rotate-180' : ''}`} />
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="space-y-6 pt-4">
              {/* Session Management */}
              <div className="space-y-6">
                {/* Upcoming Sessions */}
                <Card className="obsidian-card">
                  <CardHeader>
                    <CardTitle className="text-energy">Upcoming Sessions</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {sessions.filter(s => ['scheduled', 'in_progress'].includes(s.status)).length === 0 ? (
                        <div className="text-center py-8">
                          <Calendar className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                          <p className="text-muted-foreground">No upcoming sessions scheduled</p>
                          <p className="text-sm text-muted-foreground mt-2">
                            Sessions will appear here when students book with you
                          </p>
                        </div>
                      ) : (
                        sessions
                          .filter(s => ['scheduled', 'in_progress'].includes(s.status))
                          .map((session) => (
                            <div key={session.id} className="border rounded-lg p-4 space-y-3">
                              <div className="flex items-center justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <h4 className="font-medium">
                                      {session.profiles?.first_name} {session.profiles?.last_name}
                                    </h4>
                                    {session.is_discovery_call && (
                                      <Badge variant="secondary" className="bg-mentor-primary/20 text-mentor-primary text-xs">
                                        <PhoneCall className="w-3 h-3 mr-1" />
                                        Discovery Call
                                      </Badge>
                                    )}
                                  </div>
                                  <p className="text-sm text-muted-foreground">
                                    {new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeZone: mentorData?.timezone || getUserTimezone() }).format(new Date(session.scheduled_at))} at {new Intl.DateTimeFormat(undefined, { hour: 'numeric', minute: '2-digit', timeZone: mentorData?.timezone || getUserTimezone() }).format(new Date(session.scheduled_at))} {getTimezoneAbbr(mentorData?.timezone || getUserTimezone())}
                                  </p>
                                </div>
                                <Badge variant={session.status === 'scheduled' ? 'outline' : 'default'}>
                                  {session.status}
                                </Badge>
                              </div>
                              <div className="text-sm">
                                <p><strong>Duration:</strong> {session.duration_minutes || 60} minutes</p>
                                <p><strong>Notes:</strong> {session.notes}</p>
                                <p><strong>Student Email:</strong> {session.profiles?.first_name ? `${session.profiles.first_name} ${session.profiles.last_name}` : 'N/A'}</p>
                              </div>
                              <div className="flex gap-2 flex-wrap">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => downloadCalendarFile(session.id)}
                                  className="flex items-center gap-2"
                                >
                                  <Download className="w-4 h-4" />
                                  Add to Calendar
                                </Button>
                                {session.status === 'scheduled' && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => updateSessionStatus(session.id, 'in_progress')}
                                  >
                                    Mark as Started
                                  </Button>
                                )}
                                 {session.status === 'in_progress' && (
                                  <Button
                                    size="sm"
                                    onClick={() => setSessionToComplete(session)}
                                  >
                                    <CheckCircle className="w-4 h-4 mr-2" />
                                    Mark as Completed
                                  </Button>
                                 )}
                              </div>
                            </div>
                          ))
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Invoice Management with W9 Upload */}
              <div className="space-y-6">
                {mentorData && (
                  <W9Upload 
                    mentorId={mentorData.id}
                    currentStatus={mentorData.w9_status}
                    currentFileUrl={mentorData.w9_file_url}
                    onStatusUpdate={(status) => {
                      setMentorData(prev => prev ? {...prev, w9_status: status} : null);
                    }}
                  />
                )}
                {mentorData && <MentorInvoiceManager mentorId={mentorData.id} />}
              </div>

              {/* Visibility Toggle & Availability Management */}
              {mentorData && (
                <>
                  <MentorVisibilityToggle 
                    mentorId={mentorData.id} 
                    isActive={mentorData.is_active}
                    onVisibilityChange={(isActive) => setMentorData(prev => ({ ...prev, is_active: isActive }))}
                  />
                  
                  <DiscoveryCallToggle
                    mentorId={mentorData.id}
                    discoveryCallEnabled={mentorData.discovery_call_enabled || false}
                    onToggleChange={(enabled) => setMentorData(prev => ({ ...prev, discovery_call_enabled: enabled }))}
                  />
                </>
              )}
              
              {/* Session Programs - Custom Bundle Packages */}
              {mentorData && <MentorProgramManager mentorId={mentorData.id} />}
              
              {/* Time Blocks & Availability */}
              {mentorData && <AdvancedAvailabilityManager mentorId={mentorData.id} />}
            </CollapsibleContent>
          </Collapsible>

          {/* Profile & Settings - Now Collapsible with Change Password included */}
          <Collapsible open={profileOpen} onOpenChange={setProfileOpen}>
            <CollapsibleTrigger asChild>
              <Button variant="ghost" className="w-full justify-between text-left p-4 sm:p-6 h-auto obsidian-card hover:bg-muted/5">
                <h3 className="text-xl font-semibold text-energy">Profile & Settings</h3>
                <ChevronDown className={`h-5 w-5 text-primary transition-transform duration-200 flex-shrink-0 ${profileOpen ? 'rotate-180' : ''}`} />
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="space-y-6 pt-4">
              {/* Profile Management */}
              {mentorData && <MentorProfileEditor mentorId={mentorData.id} onProfileUpdate={handleMentorUpdate} />}

              {/* Security Settings - Change Password moved here */}
              <PasswordResetForm
                title="Change Password"
                showCard={true}
                isForForgotPassword={false}
                onSuccess={() => {
                  toast({
                    title: "Password Changed",
                    description: "Your password has been updated successfully.",
                  });
                }}
              />

              {/* Account Deactivation */}
              <AccountDeactivation />
            </CollapsibleContent>
          </Collapsible>

        </div>
      </main>

      {/* Modals */}
      {sessionToComplete && (
        <SessionCompletionModal
          isOpen={!!sessionToComplete}
          onClose={() => setSessionToComplete(null)}
          sessionId={sessionToComplete.id}
          studentName={`${sessionToComplete.profiles?.first_name || 'Student'} ${sessionToComplete.profiles?.last_name || ''}`}
          onComplete={handleCompleteSession}
        />
      )}

      {sessionToReport && (
        <ReportSessionModal
          isOpen={!!sessionToReport}
          onClose={() => setSessionToReport(null)}
          sessionId={sessionToReport.id}
          mentorName="You"
          sessionDate={new Date(sessionToReport.completed_at || sessionToReport.scheduled_at).toLocaleDateString()}
          userRole="mentor"
        />
      )}

      {/* Footer */}
      <ComplianceFooter />
    </div>
  );
};

export default MentorDashboard;